import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { ChantierPhotoUpload } from '../components/ChantierPhotoUpload';
import {
  HardHat, ChevronLeft, ChevronRight, Save, Send, Check,
  Loader2, AlertTriangle, Info, X, User, Camera, FileDown, Eye, PenTool, Upload, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { num: 1, title: 'Identification',        icon: '1' },
  { num: 2, title: 'Vérification chantier',  icon: '2' },
  { num: 3, title: 'Photos & observations',  icon: '3' },
  { num: 4, title: 'Validation finale',      icon: '4' },
];

const MARQUES   = ['WASABI', 'MME WANG', 'SORAE'];
const ENSEIGNES = ['Leclerc', 'Intermarché', 'Système U', 'Carrefour', 'Autre'];
const INTERVENANTS = ['DSAO', 'Maxime Raphaell', 'Autre'];
const NATURES   = [
  { value: 'remodeling',      label: 'Remodeling' },
  { value: 'relifting',       label: 'Relifting' },
  { value: 'nouveau_corner',  label: 'Nouveau corner' },
];

// All checklist questions - loaded from API, fallback to defaults
const DEFAULT_CHECKLIST_QUESTIONS = [
  { key: 'demontage_ancien',      label: 'Démontage d\'un ancien corner',             options: ['Oui', 'Non'],                 commentOn: [] },
  { key: 'pose_panneaux',         label: 'Pose des panneaux PLV du corner',           options: ['Oui', 'Non', 'Partiellement'], commentOn: ['Non', 'Partiellement'] },
  { key: 'pose_etageres_retail',  label: 'Pose étagères Retail & Meuble informatique', options: ['Oui', 'Non', 'Partiellement'], commentOn: ['Non', 'Partiellement'] },
  { key: 'inox_sec',              label: 'Pose inox section sec',                           options: ['Oui', 'Non', 'Partiellement'], commentOn: ['Non', 'Partiellement'] },
  { key: 'inox_froid',            label: 'Pose inox section froid',                         options: ['Oui', 'Non', 'Partiellement'], commentOn: ['Non', 'Partiellement'] },
  { key: 'mise_en_place_poubelle', label: 'Pose poubelle',                                  options: ['Oui', 'Non'],                 commentOn: [] },
  { key: 'plaintes_meubles',      label: 'Pose plinthes meubles retail & informatique',    options: ['Oui', 'Non'],                 commentOn: [] },
  { key: 'presence_equipements',  label: 'Pose complète équipements',                       options: ['Oui', 'Non'],                 commentOn: ['Non'] },
  { key: 'vitrages_peripheriques', label: 'Pose vitrages périphériques',                    options: ['Oui', 'Non'],                 commentOn: [] },
  { key: 'elements_aeriens',      label: 'Pose éléments aériens (Arche/Enseigne/PLV)',     options: ['Oui', 'Non', 'Partiellement'], commentOn: ['Non', 'Partiellement'] },
  { key: 'visuels_stickers',      label: 'Pose visuels / Stickers',                         options: ['Oui', 'Non', 'Partiellement'], commentOn: ['Non', 'Partiellement'] },
  { key: 'installation_wok',      label: 'Pose installation Wok',                           options: ['Oui', 'Non', 'Partiellement'], commentOn: ['Non', 'Partiellement'] },
  { key: 'cellule_refroidissement', label: 'Pose cellule de refroidissement',               options: ['Oui', 'Non'],                 commentOn: [] },
  { key: 'retrait_films_inox',    label: 'Retrait film inox',                          options: ['Oui', 'Non', 'Partiellement'], commentOn: ['Non', 'Partiellement'] },
  { key: 'presence_ecran',        label: 'Présence écran',                              options: ['Oui', 'Non'],                 commentOn: [] },
  { key: 'installation_ecran',    label: 'Installation écran par le prestataire',       options: ['Oui', 'Non'],                 commentOn: [] },
  { key: 'controle_froid_vitrines', label: 'Contrôle froid vitrines',                  options: ['Oui', 'Non', 'Partiellement'], commentOn: ['Non', 'Partiellement'] },
  { key: 'test_eclairage_vitrine', label: 'Test éclairage vitrine',                    options: ['Oui', 'Non', 'Partiellement'], commentOn: ['Non', 'Partiellement'] },
  { key: 'essais_plomberie',      label: 'Essais plomberie',                           options: ['Oui', 'Non', 'Partiellement'], commentOn: ['Non', 'Partiellement'] },
  { key: 'essais_electriques',    label: 'Essais électriques',                         options: ['Oui', 'Non', 'Partiellement'], commentOn: ['Non', 'Partiellement'] },
  { key: 'chantier_propre',       label: 'Chantier laissé propre',                      options: ['Oui', 'Non', 'Partiellement'], commentOn: ['Non', 'Partiellement'] },
  { key: 'elements_evacues',      label: 'Tous les éléments sont évacués de chez le client (Carton, ancien matériel...)', options: ['Oui', 'Non', 'Partiellement'], commentOn: ['Non', 'Partiellement'] },
];

/* -------- Reusable field components -------- */
function FieldGroup({ label, children, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}

function RadioRow({ label, name, value, options, onChange, required }) {
  return (
    <FieldGroup label={label} required={required}>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => {
          const val = typeof opt === 'string' ? opt : opt.value;
          const lbl = typeof opt === 'string' ? opt.charAt(0).toUpperCase() + opt.slice(1) : opt.label;
          const selected = value === val;
          return (
            <button key={val} type="button"
              onClick={() => onChange(name, val)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                selected
                  ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-orange-300 hover:bg-orange-50'
              }`}
              data-testid={`radio-${name}-${val}`}
            >{lbl}</button>
          );
        })}
      </div>
    </FieldGroup>
  );
}

function CheckboxGroup({ label, name, value = [], options, onChange }) {
  const toggle = (opt) => {
    const next = value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt];
    onChange(name, next);
  };
  return (
    <FieldGroup label={label}>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => {
          const active = value.includes(opt);
          return (
            <button key={opt} type="button" onClick={() => toggle(opt)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                active
                  ? 'bg-orange-500 text-white border-orange-500'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-orange-300'
              }`}
              data-testid={`check-${name}-${opt}`}
            >{opt}</button>
          );
        })}
      </div>
    </FieldGroup>
  );
}

/* -------- Signature Pad -------- */
function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);

  useEffect(() => {
    if (value && canvasRef.current) {
      const img = new Image();
      img.onload = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.drawImage(img, 0, 0);
      };
      img.src = value;
    }
  }, []);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };

  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e293b';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDraw = () => {
    if (drawing) {
      setDrawing(false);
      onChange(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clear = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    onChange('');
  };

  return (
    <div className="space-y-2">
      <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white relative">
        <canvas
          ref={canvasRef}
          width={500}
          height={150}
          className="w-full cursor-crosshair touch-none"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
          data-testid="signature-canvas"
        />
        {!value && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-40">
            <PenTool className="w-5 h-5 mr-2" />
            <span className="text-sm">Signez ici</span>
          </div>
        )}
      </div>
      <Button variant="outline" size="sm" onClick={clear} className="gap-1 text-xs" data-testid="clear-signature-btn">
        <Trash2 className="w-3 h-3" /> Effacer la signature
      </Button>
    </div>
  );
}

/* ========================================================= */
export default function ChantierFormPage() {
  const { id } = useParams();
  const isEdit = !!id && id !== 'new';
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [chantierId, setChantierId] = useState(isEdit ? id : null);
  const [incoherences, setIncoherences] = useState([]);
  const [orgMembers, setOrgMembers] = useState([]);
  const [scoreDetails, setScoreDetails] = useState(null);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [checklistQuestions, setChecklistQuestions] = useState(DEFAULT_CHECKLIST_QUESTIONS);

  const [form, setForm] = useState({
    marques: [], enseigne: '', nom_magasin: '', adresse: '', code_postal: '', ville: '',
    intervenants: '', intervenant_autre: '', nature_projet: '',
    intervenants_selected: [],
    // Checklist answers
    checklist: {},
    checklist_comments: {},
    // Photos
    photos: [],
    observations: '',
    // Repassage
    repassage_prevu: '',
    date_repassage: '',
    // Signature & validation
    signature_data: '',
    date_validation: new Date().toISOString().split('T')[0],
    nom_signataire: '',
    current_step: 1,
  });

  // Pre-fill nom_signataire with current user name
  useEffect(() => {
    if (user?.name && !form.nom_signataire && !isEdit) {
      setForm(prev => ({ ...prev, nom_signataire: user.name }));
    }
  }, [user?.name]);

  // ===== AUTO-SAVE BROUILLON localStorage (NEW chantier only) =====
  // Persist non-photo/signature fields so a session expiration or accidental
  // tab close doesn't wipe steps 1-3 of work in progress.
  const draftKey = useMemo(
    () => (user?.id && !isEdit ? `capex_chantier_draft_${user.id}` : null),
    [user?.id, isEdit]
  );
  const draftRestoredRef = useRef(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftRestoredFlag, setDraftRestoredFlag] = useState(false);

  // Restore on mount (NEW only)
  useEffect(() => {
    if (!draftKey || draftRestoredRef.current) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const draft = JSON.parse(raw);
        if (draft?.form && typeof draft.form === 'object') {
          setForm((prev) => ({
            ...prev,
            ...draft.form,
            // Defensive: never restore photos/signature from localStorage
            // (they're backend-owned and possibly very large)
            photos: prev.photos || [],
            signature_data: prev.signature_data || '',
          }));
          if (typeof draft.step === 'number') setStep(draft.step);
          setDraftSavedAt(draft.savedAt || null);
          setDraftRestoredFlag(true);
        }
      }
    } catch (e) {
      console.warn('Chantier draft restore failed:', e);
    } finally {
      draftRestoredRef.current = true;
    }
  }, [draftKey]);

  // Show restored toast (separated so the toast lib is ready)
  useEffect(() => {
    if (draftRestoredFlag) {
      toast.success('✏️ Brouillon restauré depuis votre dernière session', {
        duration: 5000,
        id: 'chantier-draft-restored',
      });
      setDraftRestoredFlag(false);
    }
  }, [draftRestoredFlag]);

  // Debounced auto-save on form/step change
  useEffect(() => {
    if (!draftKey || !draftRestoredRef.current) return;
    const handle = setTimeout(() => {
      try {
        // Strip large/binary fields from the persisted draft
        const { photos, signature_data, ...persistedForm } = form;
        const payload = {
          form: persistedForm,
          step,
          savedAt: new Date().toISOString(),
        };
        localStorage.setItem(draftKey, JSON.stringify(payload));
        setDraftSavedAt(payload.savedAt);
      } catch (_) { /* storage may be full */ }
    }, 800);
    return () => clearTimeout(handle);
  }, [form, step, draftKey]);

  // Manual clear
  const clearChantierDraft = useCallback(() => {
    if (!draftKey) return;
    const ok = window.confirm('Effacer le brouillon et repartir d\'un formulaire vierge ? Cette action est irréversible.');
    if (!ok) return;
    try { localStorage.removeItem(draftKey); } catch (_) { /* ignore */ }
    setForm({
      marques: [], enseigne: '', nom_magasin: '', adresse: '', code_postal: '', ville: '',
      intervenants: '', intervenant_autre: '', nature_projet: '',
      intervenants_selected: [],
      checklist: {},
      checklist_comments: {},
      photos: [],
      observations: '',
      repassage_prevu: '',
      date_repassage: '',
      signature_data: '',
      date_validation: new Date().toISOString().split('T')[0],
      nom_signataire: user?.name || '',
      current_step: 1,
    });
    setStep(1);
    setDraftSavedAt(null);
    // Re-clear after debounce window to defeat the autosave effect
    setTimeout(() => {
      try { localStorage.removeItem(draftKey); } catch (_) { /* ignore */ }
      setDraftSavedAt(null);
    }, 1000);
    toast.success('Brouillon effacé.');
  }, [draftKey, user?.name]);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Load dynamic checklist items from API
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/chantier-checklist-items`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const items = await res.json();
          if (items.length > 0) {
            setChecklistQuestions(items.map(it => ({
              key: it.key,
              label: it.label,
              options: it.options || ['Oui', 'Non', 'Partiellement'],
              commentOn: it.comment_on || [],
              category: it.category || '',
            })));
          }
        }
      } catch { /* fallback to defaults */ }
    })();
  }, [token]);

  useEffect(() => {
    // Fetch all prestataire users for "Nom de l'intervenant" dropdown
    (async () => {
      try {
        const res = await fetch(`${API}/api/users/list`, { headers });
        if (res.ok) {
          const data = await res.json();
          setOrgMembers((data || []).filter(u => u.role === 'prestataire').map(m => ({ id: m.id, name: m.name || m.email })));
        }
      } catch { /* ignore */ }
    })();
  }, [token]);

  useEffect(() => {
    if (!isEdit) { setLoading(false); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/api/chantiers/${id}`, { headers });
        if (res.ok) {
          const data = await res.json();
          // Migrate old format to new checklist format if needed
          const checklist = data.checklist || {};
          const checklist_comments = data.checklist_comments || {};
          // Migrate legacy fields
          for (const q of checklistQuestions) {
            if (!checklist[q.key] && data[q.key]) {
              checklist[q.key] = data[q.key] === 'oui' ? 'Oui' : data[q.key] === 'non' ? 'Non' : data[q.key] === 'partiel' ? 'Partiellement' : data[q.key];
            }
          }
          setForm(prev => ({ ...prev, ...data, checklist, checklist_comments }));
          setIncoherences(data.incoherences || []);
          if (data.current_step) setStep(data.current_step);
        } else { toast.error('Chantier non trouvé'); navigate('/chantiers'); }
      } catch { toast.error('Erreur réseau'); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const set = useCallback((name, value) => {
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'enseigne' && next.ville) {
        const oldAuto = prev.enseigne && prev.ville ? `${prev.enseigne} ${prev.ville}` : '';
        if (!prev.nom_magasin || prev.nom_magasin === oldAuto) next.nom_magasin = `${value} ${next.ville}`;
      }
      if (name === 'ville' && next.enseigne) {
        const oldAuto = prev.enseigne && prev.ville ? `${prev.enseigne} ${prev.ville}` : '';
        if (!prev.nom_magasin || prev.nom_magasin === oldAuto) next.nom_magasin = `${next.enseigne} ${value}`;
      }
      return next;
    });
  }, []);

  const setChecklist = useCallback((key, val) => {
    setForm(prev => ({ ...prev, checklist: { ...prev.checklist, [key]: val } }));
  }, []);

  const setChecklistComment = useCallback((key, val) => {
    setForm(prev => ({ ...prev, checklist_comments: { ...prev.checklist_comments, [key]: val } }));
  }, []);

  const toggleIntervenant = (memberName) => {
    setForm(prev => {
      const list = prev.intervenants_selected || [];
      const next = list.includes(memberName) ? list.filter(n => n !== memberName) : [...list, memberName];
      return { ...prev, intervenants_selected: next };
    });
  };

  const handleSave = async (silent = false) => {
    setSaving(true);
    try {
      // Photos & signature are persisted via their own endpoints and stored as
      // objects server-side — never re-send them in the chantier PUT payload
      // (huge base64 payload + schema mismatch would make the save fail).
      const { history, id, created_at, created_by, created_by_name, score, score_status, scored_count, status, photos, signature_data, ...cleanForm } = form;
      const payload = { ...cleanForm, current_step: step };
      let res;
      if (chantierId) {
        res = await fetch(`${API}/api/chantiers/${chantierId}`, { method: 'PUT', headers, body: JSON.stringify(payload) });
      } else {
        res = await fetch(`${API}/api/chantiers`, { method: 'POST', headers, body: JSON.stringify(payload) });
      }
      if (res.ok) {
        const data = await res.json();
        if (!chantierId) setChantierId(data.id);
        setIncoherences(data.incoherences || []);
        setForm(prev => ({ ...prev, score: data.score, score_status: data.score_status }));
        if (!silent) toast.success('Sauvegardé');
      } else {
        let errMsg = 'Erreur lors de la sauvegarde';
        try { const err = await res.json(); errMsg = err.detail || errMsg; } catch {}
        toast.error(errMsg);
      }
    } catch (err) { console.error('Save error:', err); toast.error('Erreur réseau lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleSubmit = async () => {
    if (!chantierId) { await handleSave(true); }
    setSubmitting(true);
    try {
      const cid = chantierId || form.id;
      if (!cid) { toast.error('Veuillez sauvegarder d\'abord'); setSubmitting(false); return; }
      await handleSave(true);
      const res = await fetch(`${API}/api/chantiers/${cid}/submit`, { method: 'POST', headers });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Votre rapport est enregistré et envoyé à WASABI');
        if (data.missing_fields?.length) {
          toast.info(`Rappel : ${data.missing_fields.length} élément(s) manquant(s) que vous pouvez compléter ultérieurement : ${data.missing_fields.join(', ')}`, { duration: 8000 });
        }
        if (data.incoherences?.length) {
          setIncoherences(data.incoherences);
          toast.info(`${data.incoherences.length} incohérence(s) détectée(s)`, { duration: 6000 });
        }
        // Wipe localStorage draft now that the chantier is officially submitted
        if (draftKey) {
          try { localStorage.removeItem(draftKey); } catch (_) { /* ignore */ }
        }
        navigate('/chantiers');
      } else {
        let errMsg = 'Erreur lors de la soumission';
        try { const err = await res.json(); errMsg = err.detail || errMsg; } catch {}
        toast.error(errMsg);
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Erreur réseau — veuillez réessayer');
    }
    finally { setSubmitting(false); }
  };

  const fetchScoreDetails = async () => {
    if (!chantierId) return;
    try {
      const res = await fetch(`${API}/api/chantiers/${chantierId}/score-details`, { headers });
      if (res.ok) { setScoreDetails(await res.json()); setShowScoreModal(true); }
    } catch { toast.error('Erreur chargement détails'); }
  };

  const handleExportPdf = async () => {
    const cid = chantierId || form.id;
    if (!cid) { toast.error('Veuillez sauvegarder d\'abord'); return; }
    setExportingPdf(true);
    try {
      await handleSave(true);
      const res = await fetch(`${API}/api/chantiers/${cid}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `PV_Livraison_${(form.nom_magasin || 'chantier').replace(/\s/g, '_')}.pdf`;
        a.click(); window.URL.revokeObjectURL(url);
        toast.success('PDF téléchargé');
      } else { toast.error('Erreur export PDF'); }
    } catch { toast.error('Erreur réseau'); }
    finally { setExportingPdf(false); }
  };

  const goNext = async () => { await handleSave(true); setStep(s => Math.min(s + 1, 4)); };
  const goPrev = () => setStep(s => Math.max(s - 1, 1));

  const handleAddressSelect = (addr) => {
    setForm(prev => {
      const city = addr.city || prev.ville;
      const enseigne = prev.enseigne || '';
      const autoName = enseigne && city ? `${enseigne} ${city}` : '';
      return { ...prev, adresse: addr.address || prev.adresse, code_postal: addr.postal_code || prev.code_postal, ville: city, nom_magasin: prev.nom_magasin || autoName };
    });
  };

  if (loading) return (
    <AppLayout><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div></AppLayout>
  );

  /* ----------- Step 1: Identification ----------- */
  const renderStep1 = () => (
    <div className="space-y-5">
      <CheckboxGroup label="Marque(s) du corner" name="marques" value={form.marques} options={MARQUES} onChange={set} />
      <RadioRow label="Enseigne" name="enseigne" value={form.enseigne} options={ENSEIGNES} onChange={set} required />
      <FieldGroup label="Rechercher l'adresse du magasin" required>
        <AddressAutocomplete onAddressSelect={handleAddressSelect}
          initialValue={form.adresse ? `${form.adresse}, ${form.code_postal} ${form.ville}` : ''} language="fr" label={false} />
      </FieldGroup>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FieldGroup label="Adresse"><Input value={form.adresse} onChange={e => set('adresse', e.target.value)} data-testid="field-adresse" /></FieldGroup>
        <FieldGroup label="Code postal"><Input value={form.code_postal} onChange={e => set('code_postal', e.target.value)} data-testid="field-code-postal" /></FieldGroup>
        <FieldGroup label="Ville" required><Input value={form.ville} onChange={e => set('ville', e.target.value)} data-testid="field-ville" /></FieldGroup>
      </div>
      <FieldGroup label="Nom du magasin" required>
        <Input value={form.nom_magasin} onChange={e => set('nom_magasin', e.target.value)} placeholder="Ex: Leclerc Auch" data-testid="field-nom-magasin" />
      </FieldGroup>
      <RadioRow label="Intervenants" name="intervenants" value={form.intervenants} options={INTERVENANTS} onChange={set} />
      {form.intervenants === 'Autre' && (
        <FieldGroup label="Préciser l'intervenant"><Input value={form.intervenant_autre} onChange={e => set('intervenant_autre', e.target.value)} data-testid="field-intervenant-autre" /></FieldGroup>
      )}
      <RadioRow label="Nature du projet" name="nature_projet" value={form.nature_projet} options={NATURES} onChange={set} required />
      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300"><User className="w-4 h-4 inline mr-1" />Nom de l'intervenant</Label>
        {orgMembers.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {orgMembers.map(m => {
              const selected = (form.intervenants_selected || []).includes(m.name);
              return (
                <button key={m.id} type="button" onClick={() => toggleIntervenant(m.name)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${selected ? 'bg-orange-500 text-white border-orange-500 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-orange-300 hover:bg-orange-50'}`}
                  data-testid={`intervenant-select-${m.id}`}>{m.name}</button>
              );
            })}
          </div>
        ) : (<p className="text-sm text-slate-400 italic">Aucun membre trouvé dans votre organisation</p>)}
      </div>
    </div>
  );

  /* ----------- Step 2: Checklist (20 questions) ----------- */
  const renderStep2 = () => {
    const answered = checklistQuestions.filter(q => form.checklist[q.key]).length;
    // Group questions by category
    const categories = [];
    const catMap = {};
    let globalIdx = 0;
    for (const q of checklistQuestions) {
      const cat = q.category || 'Autres';
      if (!catMap[cat]) { catMap[cat] = []; categories.push(cat); }
      catMap[cat].push({ ...q, _idx: globalIdx++ });
    }
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-slate-500">Répondez à chaque point de contrôle</p>
          <Badge variant="outline" className="text-xs">{answered}/{checklistQuestions.length} complété(s)</Badge>
        </div>
        {categories.map(cat => (
          <div key={cat} className="space-y-2">
            <div className="flex items-center gap-2 pt-2 pb-1">
              <div className={`w-1 h-5 rounded-full ${
                cat === 'Pose & Installation' ? 'bg-blue-500' :
                cat === 'Tests & Contrôles' ? 'bg-purple-500' :
                cat === 'Finitions' ? 'bg-green-500' : 'bg-slate-400'
              }`} />
              <h4 className="text-sm font-semibold text-slate-700" data-testid={`cat-heading-${cat}`}>{cat}</h4>
              <Badge variant="outline" className="text-xs">
                {catMap[cat].filter(q => form.checklist[q.key]).length}/{catMap[cat].length}
              </Badge>
            </div>
            {catMap[cat].map(q => {
              const val = form.checklist[q.key] || '';
              const showComment = q.commentOn.includes(val);
              return (
                <div key={q.key} className={`p-3 rounded-lg border transition-all ${val === 'Oui' ? 'bg-green-50 border-green-200' : val === 'Non' ? 'bg-red-50 border-red-200' : val === 'Partiellement' ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <span className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-xs font-bold shrink-0">{q._idx + 1}</span>
                      {q.label}
                    </span>
                    <div className="flex gap-1.5 shrink-0">
                      {q.options.map(opt => (
                        <button key={opt} type="button" onClick={() => { setChecklist(q.key, opt); if (!q.commentOn.includes(opt)) setChecklistComment(q.key, ''); }}
                          className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${
                            val === opt
                              ? opt === 'Oui' ? 'bg-green-500 text-white border-green-500' : opt === 'Non' ? 'bg-red-500 text-white border-red-500' : 'bg-amber-500 text-white border-amber-500'
                              : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                          }`}
                          data-testid={`check-${q.key}-${opt}`}
                        >{opt}</button>
                      ))}
                    </div>
                  </div>
                  {showComment && (
                    <div className="mt-2 ml-8">
                      <Input
                        value={form.checklist_comments[q.key] || ''}
                        onChange={e => setChecklistComment(q.key, e.target.value)}
                        placeholder={q.key === 'presence_equipements' ? 'Indiquez les éléments manquants...' : 'Commentaire...'}
                        className="text-sm"
                        data-testid={`comment-${q.key}`}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  /* ----------- Step 3: Photos & Observations ----------- */
  const renderStep3 = () => (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-700"><Camera className="w-4 h-4 inline mr-1" />Photos du chantier (max 20)</Label>
        <p className="text-xs text-slate-500">Téléchargez les photos du chantier pour documenter l'état de la livraison.</p>
        <ChantierPhotoUpload
          chantierId={chantierId}
          step={3}
          photos={form.photos || []}
          maxPhotos={20}
          onPhotoAdded={async () => {
            if (chantierId) {
              const res = await fetch(`${API}/api/chantiers/${chantierId}`, { headers });
              if (res.ok) { const data = await res.json(); setForm(prev => ({ ...prev, photos: data.photos || [] })); }
            }
          }}
        />
        <p className="text-xs text-slate-400">{(form.photos || []).length}/20 photo(s)</p>
      </div>
      <FieldGroup label="Observations">
        <Textarea value={form.observations} onChange={e => set('observations', e.target.value)}
          placeholder="Observations libres..." className="min-h-[100px]" data-testid="field-observations" />
        <Button variant="outline" size="sm" className="mt-1 text-xs" onClick={() => set('observations', 'RAS')}>Auto-remplir : RAS</Button>
      </FieldGroup>
    </div>
  );

  /* ----------- Step 4: Validation finale ----------- */
  const renderStep4 = () => (
    <div className="space-y-5">
      {/* Repassage */}
      <div className="p-4 bg-slate-50 rounded-lg border space-y-3">
        <RadioRow label="Le prestataire doit-il repasser sur le chantier ?" name="repassage_prevu" value={form.repassage_prevu}
          options={['Oui', 'Non']} onChange={set} required />
        {form.repassage_prevu === 'Oui' && (
          <div className="ml-4 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.date_repassage === 'ultérieurement'}
                onChange={e => set('date_repassage', e.target.checked ? 'ultérieurement' : '')}
                className="w-4 h-4 text-orange-500 rounded" data-testid="date-repassage-later" />
              <span className="text-sm text-slate-600">Date à définir ultérieurement</span>
            </label>
            {form.date_repassage !== 'ultérieurement' && (
              <FieldGroup label="Date prévisionnelle de repassage">
                <Input type="date" value={form.date_repassage} onChange={e => set('date_repassage', e.target.value)} data-testid="field-date-repassage" />
              </FieldGroup>
            )}
          </div>
        )}
      </div>

      <FieldGroup label="Date de l'intervention">
        <Input type="date" value={form.date_validation || new Date().toISOString().split('T')[0]} onChange={e => set('date_validation', e.target.value)} data-testid="field-date-validation" />
      </FieldGroup>

      <FieldGroup label="Nom du signataire">
        <Input value={form.nom_signataire || user?.name || ''} onChange={e => set('nom_signataire', e.target.value)} placeholder="Nom complet" data-testid="field-nom-signataire" />
      </FieldGroup>
    </div>
  );

  const stepRenderers = { 1: renderStep1, 2: renderStep2, 3: renderStep3, 4: renderStep4 };

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/chantiers')} className="gap-1"><ChevronLeft className="w-4 h-4" /> Retour</Button>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <HardHat className="w-6 h-6 text-orange-500" />
              {isEdit ? 'Modifier le chantier' : 'Nouveau chantier'}
            </h1>
          </div>
          {/* Draft auto-save indicator (NEW only) */}
          {!isEdit && draftSavedAt && (
            <div className="flex items-center gap-2 ml-auto">
              <div
                className="hidden sm:flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 px-2.5 py-1 rounded-full"
                title={`Brouillon sauvegardé automatiquement le ${new Date(draftSavedAt).toLocaleString('fr-FR')}`}
                data-testid="chantier-draft-saved-badge"
              >
                <Save className="w-3 h-3" />
                <span className="font-medium">Brouillon enregistré</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearChantierDraft}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 gap-1.5"
                data-testid="chantier-clear-draft-btn"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Effacer brouillon</span>
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 overflow-x-auto pb-2" data-testid="step-progress">
          {STEPS.map((s, i) => {
            const done = step > s.num;
            const active = step === s.num;
            return (
              <React.Fragment key={s.num}>
                <button type="button" onClick={() => setStep(s.num)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium shrink-0 transition-all ${
                    active ? 'bg-orange-500 text-white shadow-md' : done ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`} data-testid={`step-btn-${s.num}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${active ? 'bg-white/20 text-white' : done ? 'bg-green-500 text-white' : 'bg-slate-300 text-white'}`}>
                    {done ? <Check className="w-3.5 h-3.5" /> : s.num}
                  </span>
                  <span className="hidden sm:inline">{s.title}</span>
                </button>
                {i < STEPS.length - 1 && <div className={`w-4 h-0.5 shrink-0 ${done ? 'bg-green-400' : 'bg-slate-200'}`} />}
              </React.Fragment>
            );
          })}
        </div>

        <Card className="border-orange-100" data-testid="chantier-form-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-orange-700 flex items-center gap-2">
              Etape {step} : {STEPS[step - 1].title}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">{stepRenderers[step]?.()}</CardContent>
        </Card>

        <div className="flex items-center justify-between gap-3">
          <Button variant="outline" onClick={goPrev} disabled={step === 1} className="gap-1" data-testid="prev-step-btn">
            <ChevronLeft className="w-4 h-4" /> Précédent
          </Button>
          <div className="flex items-center gap-2">
            {chantierId && (
              <Button variant="outline" onClick={handleExportPdf} disabled={exportingPdf} className="gap-1" data-testid="export-pdf-btn">
                {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} PDF
              </Button>
            )}
            <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} className="gap-1" data-testid="save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Sauvegarder
            </Button>
            {step === 4 ? (
              <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white gap-1" data-testid="submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Soumettre
              </Button>
            ) : (
              <Button onClick={goNext} className="bg-orange-500 hover:bg-orange-600 text-white gap-1" data-testid="next-step-btn">
                Suivant <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Score Details Modal */}
      <Dialog open={showScoreModal} onOpenChange={setShowScoreModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="score-detail-modal">
          <DialogHeader><DialogTitle className="text-orange-700 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Détail du score</DialogTitle></DialogHeader>
          {scoreDetails && (
            <div className="space-y-4">
              <div className={`p-3 rounded-lg text-center ${scoreDetails.score_status === 'conforme' ? 'bg-green-50 border border-green-200' : scoreDetails.score_status === 'a_corriger' ? 'bg-orange-50 border border-orange-200' : 'bg-red-50 border border-red-200'}`}>
                <p className="text-2xl font-bold">{scoreDetails.score}%</p>
                <p className="text-sm">{scoreDetails.conformes_count} conforme(s) / {scoreDetails.total_fields} éléments</p>
              </div>
              {scoreDetails.to_correct?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-red-700 mb-2">Eléments à corriger ({scoreDetails.to_correct_count})</h4>
                  <div className="space-y-1.5">
                    {scoreDetails.to_correct.map((item, i) => (
                      <div key={i} className={`flex items-center justify-between p-2.5 rounded-lg border text-sm ${item.status === 'non_conforme' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                        <span className="font-medium">{item.label}</span>
                        <Badge variant="outline" className="text-xs">{item.value}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {scoreDetails.details?.filter(d => d.status === 'conforme').length > 0 && (
                <div>
                  <h4 className="font-semibold text-green-700 mb-2">Eléments conformes ({scoreDetails.conformes_count})</h4>
                  <div className="space-y-1">
                    {scoreDetails.details.filter(d => d.status === 'conforme').map((item, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-green-50 border border-green-100 text-sm">
                        <span>{item.label}</span><Check className="w-4 h-4 text-green-600" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
